import {
  useCallback, useEffect, useState,
} from 'react'
import type {
  IntegrationInstance,
} from '../services/api'

// Minimal slice of an integration service this hook needs. The OAuth
// integration services created by `createIntegrationService` satisfy it.
interface InstanceService {
  listInstances: () => Promise<IntegrationInstance[]>
  disconnect: (label?: string) => Promise<unknown>
}

export interface UseIntegrationInstances {
  instances: IntegrationInstance[]
  loading: boolean
  reload: () => Promise<void>
  remove: (label: string) => Promise<void>
}

// Tracks the connected accounts (instances) of one integration type and
// supports removing a specific account by label. Loads on mount; callers
// should also call `reload()` after a successful connect so a freshly added
// account appears.
export function useIntegrationInstances(
  service: InstanceService,
): UseIntegrationInstances {
  const [instances, setInstances] = useState<IntegrationInstance[]>([])
  const [loading, setLoading] = useState(false)

  const reload = useCallback(async () => {
    try {
      setInstances(await service.listInstances())
    } catch (error) {
      console.error('Failed to load integration accounts:', error)
    }
  }, [service])

  useEffect(() => {
    void reload()
  }, [reload])

  const remove = useCallback(
    async (label: string) => {
      setLoading(true)
      try {
        await service.disconnect(label)
        await reload()
      } catch (error) {
        console.error('Failed to remove integration account:', error)
      } finally {
        setLoading(false)
      }
    },
    [service, reload],
  )

  return {
    instances, loading, reload, remove,
  }
}
